import React from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@hooks/useAuth';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import { APP_NAME } from '@/config/appConfig';
import Button from '@components/common/atoms/AppButton';
import Icon from '@components/common/atoms/Icon';
import { STATS } from '../../constants/landingPageConstants';

const LandingHero: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const primaryCtaLabel = isAuthenticated ? 'Go to Dashboard' : 'Sign In';
  const goToApp = () => navigate(isAuthenticated ? '/dashboard' : '/login');

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box component="section" className="relative overflow-hidden bg-gradient-to-br from-indigo-950 via-purple-900 to-violet-950">
      {/* Floating decorative blobs (match login left panel) */}
      <Box className="pointer-events-none absolute inset-0 overflow-hidden">
        <Box className="animate-float absolute -top-16 left-10 h-72 w-72 rounded-full bg-indigo-500/20 blur-3xl" />
        <Box
          className="animate-float absolute bottom-0 right-0 h-96 w-96 rounded-full bg-fuchsia-500/20 blur-3xl"
          style={{ animationDelay: '1.2s' }}
        />
        <Box className="absolute left-1/2 top-1/3 h-80 w-80 -translate-x-1/2 rounded-full bg-violet-500/10 blur-3xl animate-pulse" />
      </Box>

      <Box className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <Box className="mx-auto max-w-3xl text-center text-white">
          <Typography component="span" className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold backdrop-blur-xl">
            <Icon name="Sparkles" size={14} className="text-amber-300" />
            Empowering People. Elevating Organisations.
          </Typography>

          <Typography component="h1" className="mt-6 text-4xl font-bold leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl">
            The modern HR platform your{' '}
            <Typography component="span" className="bg-gradient-to-r from-indigo-300 via-fuchsia-300 to-amber-200 bg-clip-text text-transparent">
              whole team
            </Typography>{' '}
            will love
          </Typography>

          <Typography component="p" className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-white/80">
            {APP_NAME} brings attendance, leave, onboarding, assets and more into one secure,
            beautifully simple system — so you can streamline operations and focus on what
            truly matters: your people.
          </Typography>

          <Box className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button
              variant="primary"
              size="lg"
              onClick={goToApp}
              className="w-full sm:w-auto shadow-xl shadow-indigo-900/40"
            >
              <Icon name="LogIn" size={18} className="mr-2" />
              {primaryCtaLabel}
            </Button>
            <MuiButton
              onClick={() => scrollTo('features')}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border-2 border-white/25 bg-white/5 px-8 py-3 text-base font-semibold text-white backdrop-blur-xl transition-all hover:bg-white/10 sm:w-auto"
            >
              Explore features
              <Icon name="ArrowDown" size={18} />
            </MuiButton>
          </Box>

          {/* Stat strip */}
          <Box className="mx-auto mt-14 grid max-w-3xl grid-cols-2 gap-4 sm:grid-cols-4">
            {STATS.map((stat) => (
              <Box
                key={stat.label}
                className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-xl"
              >
                <div className="text-2xl font-bold text-white sm:text-3xl">{stat.value}</div>
                <div className="mt-1 text-xs font-medium text-white/60">{stat.label}</div>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Soft fade into the next section */}
      <Box className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white to-transparent dark:from-slate-950" />
    </Box>
  );
};

export default LandingHero;
